import {
  ScaffolderFieldError,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import React from 'react';

import { Box, MenuItem, TextField } from '@material-ui/core';

import {
  HELM_CONFIG_INFO,
  KUSTOMIZE_CONFIG_INFO,
  MANIFESTS_CONFIG_INFO,
} from '../infoDoc';
import { useStyles } from '../styles';
import {
  DEFAULT_HELM_CONFIGURATION,
  RepoType,
  SourceConfiguration,
} from '../types';
import { inferRepoType, resolveRepoType } from '../utils';
import {
  containsTemplateExpression,
  validateSourceGitURL,
  validateSourceInputPattern,
  validateSourceRepoURL,
  validateSourceRevision,
  validateHelmReleaseName,
} from '../validation';
import { RepoUrlPicker } from './RepoUrlPicker';

const REPO_TYPE_LABELS: Record<RepoType, string> = {
  git: 'Git',
  helm: 'Helm',
  oci: 'Helm (OCI)',
};

const DEPLOYMENT_TYPE_CONFIG = {
  manifests: {
    label: 'Manifests',
    description: 'Source for the Kubernetes manifests.',
    infoDoc: MANIFESTS_CONFIG_INFO,
    repoURLHelperText:
      'TomTom Git repository URL (tomtom-internal or tomtom-international GitHub orgs).',
    pathLabel: 'Manifests Path',
    pathHelperText: 'Repository path to the manifests directory.',
    pathErrorMessage:
      'Manifests Path must contain only letters, numbers, dots, hyphens, underscores, or slashes.',
  },
  helm: {
    label: 'Helm',
    description: 'Source for the Helm chart.',
    infoDoc: HELM_CONFIG_INFO,
    repoURLHelperText: (
      <>
        Source repository URL (Git, Helm, or OCI registry). See the{' '}
        <strong>
          <a
            href="https://github.com/tomtom-internal/deployment-as-a-service/blob/main/docs/tools/allowlist.md"
            target="_blank"
            rel="noreferrer"
          >
            allowed URL prefixes
          </a>
        </strong>
        .
      </>
    ),
    pathLabel: 'Chart Path',
    pathHelperText: 'Repository path to the Helm chart directory.',
    pathErrorMessage:
      'Chart Path must contain only letters, numbers, dots, hyphens, underscores, or slashes.',
  },
  kustomize: {
    label: 'Kustomize',
    description: 'Source for the Kustomization.',
    infoDoc: KUSTOMIZE_CONFIG_INFO,
    repoURLHelperText:
      'TomTom Git repository URL (tomtom-internal or tomtom-international GitHub orgs).',
    pathLabel: 'Kustomization Path',
    pathHelperText: 'Repository path to the kustomization directory.',
    pathErrorMessage:
      'Kustomization Path must contain only letters, numbers, dots, hyphens, underscores, or slashes.',
  },
} as const;

interface SourceFieldsProps {
  source: SourceConfiguration;
  onChange: (value: SourceConfiguration) => void;
  enableAppSetSupport: boolean;
  errors: string[];
}

export const SourceFields = ({
  source,
  onChange,
  enableAppSetSupport,
  errors,
}: SourceFieldsProps) => {
  const classes = useStyles();

  const inferred = inferRepoType(source.repoURL);
  const repoType = resolveRepoType(source);

  const typeConfig = source.deploymentType
    ? DEPLOYMENT_TYPE_CONFIG[source.deploymentType]
    : DEPLOYMENT_TYPE_CONFIG.helm;

  const writeURL = (url: string) => {
    onChange({ ...source, repoURL: url });
  };

  const handleFieldChange = <K extends keyof SourceConfiguration>(
    key: K,
    v: SourceConfiguration[K],
  ) => {
    onChange({ ...source, [key]: v });
  };

  const handleReleaseNameChange = (releaseName: string) => {
    const helm = source.helm ?? DEFAULT_HELM_CONFIGURATION;
    onChange({ ...source, helm: { ...helm, releaseName } });
  };

  let repoURLErrorNode: React.ReactNode = null;
  if (Boolean(errors?.length > 0)) {
    const isGitOnly =
      source.deploymentType === 'manifests' ||
      source.deploymentType === 'kustomize';
    if (
      isGitOnly &&
      !containsTemplateExpression(source.repoURL) &&
      !validateSourceGitURL(source.repoURL)
    ) {
      repoURLErrorNode = (
        <ScaffolderFieldError message="Source must be a valid TomTom repository from tomtom-internal or tomtom-international GitHub orgs." />
      );
    } else if (
      !isGitOnly &&
      !validateSourceRepoURL(source.repoURL, { enableAppSetSupport })
    ) {
      repoURLErrorNode = (
        <ScaffolderFieldError
          message={
            <>
              Source repository URL must match one of the{' '}
              <strong>
                <a
                  href="https://github.com/tomtom-internal/deployment-as-a-service/blob/main/docs/tools/allowlist.md"
                  target="_blank"
                  rel="noreferrer"
                >
                  allowed URL prefixes
                </a>
              </strong>
              .
            </>
          }
        />
      );
    }
  }

  return (
    <Box>
      <Box className={classes.inputContainer}>
        <ScaffolderInputHeader
          title={`${typeConfig.label} Configuration`}
          description={typeConfig.description}
          infoDoc={typeConfig.infoDoc}
        />
      </Box>

      <Box className={classes.inputContainer}>
        <RepoUrlPicker
          value={source.repoURL}
          onChange={writeURL}
          label="Repository URL"
          required
          error={
            Boolean(errors?.length > 0) &&
            (!validateSourceRepoURL(source.repoURL, { enableAppSetSupport }) ||
              ((source.deploymentType === 'manifests' ||
                source.deploymentType === 'kustomize') &&
                !containsTemplateExpression(source.repoURL) &&
                !validateSourceGitURL(source.repoURL)))
          }
          freeSolo
          helperText={repoURLErrorNode ?? typeConfig.repoURLHelperText}
        />
      </Box>

      {source.repoURL.trim() !== '' &&
        inferred === null &&
        source.deploymentType === 'helm' &&
        enableAppSetSupport && (
          <Box className={classes.inputContainer}>
            <TextField
              select
              label="Repo Type"
              required
              variant="outlined"
              color="secondary"
              margin="none"
              size="small"
              fullWidth
              value={source.repoType ?? 'git'}
              onChange={e =>
                handleFieldChange('repoType', e.target.value as RepoType)
              }
              helperText="Type of the templated repository URL (could not be inferred from the URL input)."
            >
              <MenuItem value="git">{REPO_TYPE_LABELS.git}</MenuItem>
              <MenuItem value="helm">{REPO_TYPE_LABELS.helm}</MenuItem>
              <MenuItem value="oci">{REPO_TYPE_LABELS.oci}</MenuItem>
            </TextField>
          </Box>
        )}

      {repoType === 'git' && (
        <>
          <Box className={classes.inputContainer}>
            <TextField
              label="Revision"
              required
              variant="outlined"
              color="secondary"
              margin="none"
              size="small"
              fullWidth
              value={source.targetRevision}
              onChange={e =>
                handleFieldChange('targetRevision', e.target.value)
              }
              error={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(source.targetRevision, {
                  enableAppSetSupport,
                })
              }
              helperText={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(source.targetRevision, {
                  enableAppSetSupport,
                }) ? (
                  <ScaffolderFieldError message="Revision must be a Git branch, commit hash, tag, or semver constraint." />
                ) : (
                  'The Git revision to deploy (a branch, tag, commit hash, or a semver constraint).'
                )
              }
            />
          </Box>
          <Box className={classes.inputContainer}>
            <TextField
              label={typeConfig.pathLabel}
              required
              variant="outlined"
              color="secondary"
              margin="none"
              size="small"
              fullWidth
              value={source.path ?? ''}
              onChange={e => handleFieldChange('path', e.target.value)}
              error={
                Boolean(errors?.length > 0) &&
                (!source.path ||
                  !validateSourceInputPattern(source.path, {
                    enableAppSetSupport,
                  }))
              }
              helperText={
                Boolean(errors?.length > 0) &&
                (!source.path ||
                  !validateSourceInputPattern(source.path, {
                    enableAppSetSupport,
                  })) ? (
                  <ScaffolderFieldError message={typeConfig.pathErrorMessage} />
                ) : (
                  typeConfig.pathHelperText
                )
              }
            />
          </Box>
        </>
      )}

      {repoType === 'helm' && (
        <>
          <Box className={classes.inputContainer}>
            <TextField
              label="Chart Name"
              required
              variant="outlined"
              color="secondary"
              margin="none"
              size="small"
              fullWidth
              value={source.chart ?? ''}
              onChange={e => handleFieldChange('chart', e.target.value)}
              error={
                Boolean(errors?.length > 0) &&
                (!source.chart ||
                  !validateSourceInputPattern(source.chart, {
                    enableAppSetSupport,
                  }))
              }
              helperText={
                Boolean(errors?.length > 0) &&
                (!source.chart ||
                  !validateSourceInputPattern(source.chart, {
                    enableAppSetSupport,
                  })) ? (
                  <ScaffolderFieldError message="Chart name must contain only letters, numbers, dots, hyphens, underscores, or slashes." />
                ) : (
                  'Name of the Helm chart in the registry (e.g. app or airflow).'
                )
              }
            />
          </Box>
          <Box className={classes.inputContainer}>
            <TextField
              label="Chart Version"
              required
              variant="outlined"
              color="secondary"
              margin="none"
              size="small"
              fullWidth
              value={source.targetRevision}
              onChange={e =>
                handleFieldChange('targetRevision', e.target.value)
              }
              error={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(source.targetRevision, {
                  enableAppSetSupport,
                })
              }
              helperText={
                Boolean(errors?.length > 0) &&
                !validateSourceRevision(source.targetRevision, {
                  enableAppSetSupport,
                }) ? (
                  <ScaffolderFieldError message="Chart version must be a valid version, git revision or semver constraint." />
                ) : (
                  'Helm chart version (e.g. 1.0.0), a semver constraint to track (e.g. 1.2.*), or git revision (if chart is stored in git).'
                )
              }
            />
          </Box>
        </>
      )}

      {repoType === 'oci' && (
        <Box className={classes.inputContainer}>
          <TextField
            label="Chart Version"
            required
            variant="outlined"
            color="secondary"
            margin="none"
            size="small"
            fullWidth
            value={source.targetRevision}
            onChange={e => handleFieldChange('targetRevision', e.target.value)}
            error={
              Boolean(errors?.length > 0) &&
              !validateSourceRevision(source.targetRevision, {
                enableAppSetSupport,
              })
            }
            helperText={
              Boolean(errors?.length > 0) &&
              !validateSourceRevision(source.targetRevision, {
                enableAppSetSupport,
              }) ? (
                <ScaffolderFieldError message="Chart version must be a valid version, git revision or semver constraint." />
              ) : (
                'Helm chart version (e.g. 1.0.0), or a semver constraint to track (e.g. 1.2.*).'
              )
            }
          />
        </Box>
      )}

      {source.deploymentType === 'helm' && (
        <Box className={classes.inputContainer}>
          <TextField
            label="Release Name"
            required
            variant="outlined"
            color="secondary"
            margin="none"
            size="small"
            fullWidth
            value={source.helm?.releaseName ?? ''}
            onChange={e => handleReleaseNameChange(e.target.value)}
            error={
              Boolean(errors?.length > 0) &&
              (!source.helm?.releaseName ||
                !validateHelmReleaseName(source.helm.releaseName, {
                  enableAppSetSupport,
                }))
            }
            helperText={
              Boolean(errors?.length > 0) &&
              (!source.helm?.releaseName ||
                !validateHelmReleaseName(source.helm.releaseName, {
                  enableAppSetSupport,
                })) ? (
                <ScaffolderFieldError message="Release name must be lowercase alphanumeric and hyphens (max 53 characters)." />
              ) : (
                'Helm release name. Lowercase alphanumeric and hyphens, max 53 characters.'
              )
            }
          />
        </Box>
      )}
    </Box>
  );
};
